import Swiper from 'swiper';
import { Navigation, Pagination, Mousewheel } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import safeJSONParse from '../utils/safeJsonParse';
import { BREAKPOINTS } from '../utils/constants';

declare global {
  interface Window {
    wobbletree: {
      swiperObjects: Swiper[];
      swiperMobileObjects: Swiper[];
    };
  }
}

const initSwiper = (): void => {
  const swipers = document.querySelectorAll<HTMLElement>('[data-swiper]');
  const thumbSwipers: Swiper[] = [];

  if (!swipers.length) return;

  const wobbletree = (window.wobbletree ??= {
    swiperObjects: [],
    swiperMobileObjects: [],
  });
  wobbletree.swiperObjects = [];

  swipers.forEach((swiper): void => {
    const rawConfig = swiper.dataset.swiperConfig ?? '';
    const parsedConfig = rawConfig
      ? (safeJSONParse(rawConfig, false) as SwiperOptions | false | null)
      : {};

    if (parsedConfig === false || parsedConfig === null || typeof parsedConfig !== 'object') {
      console.error('Error in Swiper JSON config:', rawConfig);
      return;
    }

    const swiperModules: NonNullable<SwiperOptions['modules']> = [];
    if ('navigation' in parsedConfig) swiperModules.push(Navigation);
    if ('pagination' in parsedConfig) swiperModules.push(Pagination);
    if ('mousewheel' in parsedConfig) swiperModules.push(Mousewheel);

    const finalConfig: SwiperOptions = {
      ...parsedConfig,
      modules: swiperModules,
    };

    swiper.classList.add('swiper--active');
    const newSwiper = new Swiper(swiper, finalConfig);

    if (swiper.dataset.swiperThumbs != null) {
      thumbSwipers.push(newSwiper);
    }
    wobbletree.swiperObjects.push(newSwiper);
  });
};

const initSwiperMobile = (): void => {
  const swipers = document.querySelectorAll<HTMLElement>('[data-swiper-mob]');
  if (!swipers.length) return;

  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);

  const wobbletree = (window.wobbletree ??= {
    swiperObjects: [],
    swiperMobileObjects: [],
  });
  wobbletree.swiperMobileObjects = [];

  const initSwiperOnMobile = (): void => {
    if (desktopMediaQuery.matches) {
      wobbletree.swiperMobileObjects.forEach((swiper: Swiper): void => {
        swiper.destroy(true, true);
      });
      wobbletree.swiperMobileObjects = [];
      return;
    }

    swipers.forEach((swiper): void => {
      const rawConfig = swiper.dataset.swiperConfig ?? '';
      const parsedConfig = rawConfig
        ? (safeJSONParse(rawConfig, false) as SwiperOptions | false | null)
        : {};

      if (parsedConfig === false || parsedConfig === null || typeof parsedConfig !== 'object') {
        console.error('Error in Swiper JSON config:', rawConfig);
        return;
      }

      const swiperModules: NonNullable<SwiperOptions['modules']> = [];

      if ('navigation' in parsedConfig) swiperModules.push(Navigation);
      if ('pagination' in parsedConfig) swiperModules.push(Pagination);

      const finalConfig: SwiperOptions = {
        ...parsedConfig,
        modules: swiperModules,
      };

      swiper.classList.add('swiper--mob-active');
      const newSwiper = new Swiper(swiper, finalConfig);
      wobbletree.swiperMobileObjects.push(newSwiper);
    });
  };

  initSwiperOnMobile();
  desktopMediaQuery.addEventListener('change', initSwiperOnMobile);
};

export { initSwiper, initSwiperMobile };
